// Updated issuetype.jsx - Fixed to use correct Material-UI icon syntax with colors
import React from 'react';
import {
  AlertTriangle as AlertTriangleIcon,
  ArrowUp as ArrowUpIcon,
  ArrowDown as ArrowDownIcon,
  Minus as MinusIcon,
  Circle as CircleIcon
} from 'lucide-react';
import {
  BugReport as BugIcon, 
  Task as TaskIcon,
  Book as StoryIcon, 
  Flag as EpicIcon, 
  Edit as ChangeRequestIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Issue Types - Fixed with correct MUI icon color syntax
export const issueTypes = [
  {
    id: 1,
    name: 'Bug',
    icon: <BugIcon sx={{ fontSize: 20, color: '#ff8a50' }} />,
    color: '#ff8a50', 
    description: 'Something that is broken and needs to be fixed'
  },
  {
    id: 2,
    name: 'Epic',
    icon: <EpicIcon sx={{ fontSize: 20, color: '#9c27b0' }} />,
    color: '#9c27b0',
    description: 'A large body of work that can be broken down into stories'
  },
  {
    id: 3,
    name: 'Story',
    icon: <StoryIcon sx={{ fontSize: 20, color: '#2196f3' }} />,
    color: '#2196f3',
    description: 'A user story or feature requirement'
  },
  {
    id: 4,
    name: 'Task',
    icon: <TaskIcon sx={{ fontSize: 20, color: '#4caf50' }} />,
    color: '#4caf50',
    description: 'A specific piece of work to be done'
  },

];

// Status Types
export const statuses = [
  {
    id: 1,
    name: 'To Do',
    color: '#718096',
    description: 'Work that has not been started'
  },
  {
    id: 2,
    name: 'In Progress',
    color: '#3182ce',
    description: 'Work that is actively being worked on'
  },
  {
    id: 3,
    name: 'In Review',
    color: '#d69e2e',
    description: 'Work that is completed and awaiting review'
  },
  {
    id: 4,
    name: 'Done',
    color: '#38a169',
    description: 'Work that has been completed'
  },
  {
    id: 5,
    name: 'Blocked',
    color: '#e53e3e',
    description: 'Work that cannot proceed due to impediments'
  }
];

export const getIssueTypeName = (issueTypeData) => {
  if (!issueTypeData) return 'task';
  
  // If it's already a string, return lowercase
  if (typeof issueTypeData === 'string') {
    return issueTypeData.toLowerCase();
  }
  
  // If it's a number (ID), find the corresponding name
  if (typeof issueTypeData === 'number') {
    const type = getIssueTypeById(issueTypeData);
    return type.name.toLowerCase();
  }
  
  // If it's an object with name property
  if (typeof issueTypeData === 'object' && issueTypeData.name) {
    return issueTypeData.name.toLowerCase();
  }
  
  // If it's an object with id property
  if (typeof issueTypeData === 'object' && issueTypeData.id) {
    const type = getIssueTypeById(issueTypeData.id);
    return type.name.toLowerCase();
  }
  
  return 'task'; // Default fallback
};

// Bug-specific statuses
export const bugStatuses = [
  {
    id: 1,
    name: 'Open',
    color: '#e53e3e',
    description: 'Bug has been reported and needs attention'
  },
  {
    id: 2,
    name: 'In Progress',
    color: '#3182ce',
    description: 'Bug is being investigated or fixed'
  },
  {
    id: 3,
    name: 'Fixed',
    color: '#38a169',
    description: 'Bug has been fixed and is ready for testing'
  },
  {
    id: 4,
    name: 'Verified',
    color: '#38a169',
    description: 'Bug fix has been tested and verified'
  },
  {
    id: 5,
    name: 'Closed',
    color: '#718096',
    description: 'Bug has been resolved and closed'
  },
  {
    id: 6,
    name: 'Rejected',
    color: '#a0aec0',
    description: 'Bug report was invalid or not reproducible'
  },
  {
    id: 7,
    name: 'Duplicate',
    color: '#a0aec0',
    description: 'Bug is a duplicate of another existing bug'
  },
  {
    id: 8,
    name: 'Reopened',
    color: '#ed8936',
    description: 'Previously closed bug has been reopened'
  }
];

// Priority levels - Existing 1-5 unchanged, new entries starting from 6
export const priorities = [
  {
    id: 1,
    name: 'Highest',
    icon: <ArrowUpIcon size={16} color="#e53e3e" />,
    color: '#e53e3e',
    description: 'Critical priority - immediate attention required'
  },
  {
    id: 2,
    name: 'High',
    icon: <ArrowUpIcon size={16} color="#ed8936" />,
    color: '#ed8936',
    description: 'High priority - should be addressed soon'
  },
  {
    id: 3,
    name: 'Medium',
    icon: <MinusIcon size={16} color="#d69e2e" />,
    color: '#d69e2e',
    description: 'Medium priority - normal priority level'
  },
  {
    id: 4,
    name: 'Low',
    icon: <ArrowDownIcon size={16} color="#38a169" />,
    color: '#38a169',
    description: 'Low priority - can be addressed when time permits'
  },
  {
    id: 5,
    name: 'Lowest',
    icon: <ArrowDownIcon size={16} color="#718096" />,
    color: '#718096',
    description: 'Lowest priority - nice to have'
  },
  {
    id: 6,
    name: 'Normal',
    icon: <MinusIcon size={16} color="#d69e2e" />,
    color: '#d69e2e',
    description: 'Normal priority - standard level'
  },
  {
    id: 7,
    name: 'P1',
    icon: <ArrowUpIcon size={16} color="#e53e3e" />,
    color: '#e53e3e',
    description: 'P1 priority - highest severity, immediate action'
  },
  {
    id: 8,
    name: 'P2',
    icon: <ArrowUpIcon size={16} color="#ed8936" />,
    color: '#ed8936',
    description: 'P2 priority - high severity, urgent'
  },
  {
    id: 9,
    name: 'P3',
    icon: <MinusIcon size={16} color="#d69e2e" />,
    color: '#d69e2e',
    description: 'P3 priority - medium severity'
  },
  {
    id: 10,
    name: 'P4',
    icon: <ArrowDownIcon size={16} color="#38a169" />,
    color: '#38a169',
    description: 'P4 priority - low severity'
  },
  {
    id: 11,
    name: 'P5',
    icon: <ArrowDownIcon size={16} color="#718096" />,
    color: '#718096',
    description: 'P5 priority - lowest severity'
  }
];


export const severities = [
  {
    id: 1,
    name: 'Critical',
    icon: <AlertTriangleIcon size={16} color="#e53e3e" />,
    color: '#e53e3e',
    description: 'System crashes, data loss, or major functionality broken'
  },
  {
    id: 2,
    name: 'High',
    icon: <AlertTriangleIcon size={16} color="#ed8936" />,
    color: '#ed8936',
    description: 'Major functionality impacted, workaround available'
  },
  {
    id: 3,
    name: 'Medium',
    icon: <CircleIcon size={16} color="#d69e2e" />,
    color: '#d69e2e',
    description: 'Minor functionality impacted'
  },
  {
    id: 4,
    name: 'Low',
    icon: <CircleIcon size={16} color="#38a169" />,
    color: '#38a169',
    description: 'Cosmetic issues or minor inconveniences'
  },
  {
    id: 5,
    name: 'Trivial',
    icon: <CircleIcon size={16} color="#718096" />,
    color: '#718096',
    description: 'Very minor issues with minimal impact'
  }
];

// Label colors for creating new labels
export const labelColors = [
  { name: 'Blue', value: '#0052cc' },
  { name: 'Green', value: '#00875a' },
  { name: 'Purple', value: '#8777d9' },
  { name: 'Red', value: '#de350b' },
  { name: 'Orange', value: '#ff7452' },
  { name: 'Yellow', value: '#ffab00' },
  { name: 'Teal', value: '#00a3bf' },
  { name: 'Pink', value: '#e774bb' },
  { name: 'Gray', value: '#6b778c' },
  { name: 'Dark Blue', value: '#0747a6' },
  { name: 'Dark Green', value: '#006644' },
  { name: 'Dark Purple', value: '#5243aa' },
  { name: 'Dark Red', value: '#b91c1c' },
  { name: 'Dark Orange', value: '#ea580c' },
  { name: 'Dark Yellow', value: '#d97706' },
  { name: 'Dark Teal', value: '#0e7490' },
  { name: 'Dark Pink', value: '#be185d' },
  { name: 'Dark Gray', value: '#374151' }
];

// Mock hierarchical data structure (if needed for development/testing)
export const mockHierarchicalData = {
  projects: [
    {
      id: 1,
      project_id: 1,
      name: 'E-Commerce Platform',
      key: 'ECP',
      lead_id: 'EMP001',
      lead_name: 'John Smith',
      epics: [
        {
          id: 1,
          name: 'User Authentication System',
          description: 'Complete user login and registration system',
          stories: [
            {
              id: 1,
              name: 'User Login',
              description: 'Allow users to log into the system',
              tasks: [
                {
                  id: 1,
                  name: 'Create Login Form',
                  type: 'Task',
                  description: 'Design and implement login form UI'
                },
                {
                  id: 2,
                  name: 'Implement Authentication Logic',
                  type: 'Task',
                  description: 'Backend authentication logic'
                }
              ]
            },
            {
              id: 2,
              name: 'User Registration',
              description: 'Allow new users to create accounts',
              tasks: [
                {
                  id: 3,
                  name: 'Create Registration Form',
                  type: 'Task',
                  description: 'Design and implement registration form'
                }
              ]
            }
          ]
        },
        {
          id: 2,
          name: 'Shopping Cart Functionality',
          description: 'Shopping cart and checkout process',
          stories: [
            {
              id: 3,
              name: 'Add to Cart',
              description: 'Users can add products to shopping cart',
              tasks: [
                {
                  id: 4,
                  name: 'Cart UI Components',
                  type: 'Task',
                  description: 'Create cart display components'
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 2,
      project_id: 2,
      name: 'Mobile Banking App',
      key: 'MBA',
      lead_id: 'EMP002',
      lead_name: 'Sarah Johnson',
      epics: [
        {
          id: 3,
          name: 'Account Management',
          description: 'User account and profile management',
          stories: [
            {
              id: 4,
              name: 'View Account Balance',
              description: 'Display current account balance',
              tasks: [
                {
                  id: 5,
                  name: 'Balance Display Component',
                  type: 'Task',
                  description: 'Create component to show account balance'
                }
              ]
            }
          ]
        }
      ]
    }
  ]
};

// Helper functions
export const getIssueTypeById = (id) => {
  return issueTypes.find(type => type.id === parseInt(id));
};

export const getStatusById = (id) => {
  return statuses.find(status => status.id === parseInt(id));
};

export const getBugStatusById = (id) => {
  return bugStatuses.find(status => status.id === parseInt(id));
};

export const getPriorityById = (id) => {
  return priorities.find(priority => priority.id === parseInt(id));
};

export const getSeverityById = (id) => {
  return severities.find(severity => severity.id === parseInt(id));
};

// Export default object with all constants
export default {
  issueTypes,
  statuses,
  bugStatuses,
  priorities,
  severities,
  labelColors,
  mockHierarchicalData,
  getIssueTypeById,
  getStatusById,
  getBugStatusById,
  getPriorityById,
  getSeverityById, };